import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Image, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from '@/src/i18n';
import { createStyles } from './WelcomeScreen.styles';

const FEATURES: { icon: 'trophy' | 'star' | 'flash'; color: string; key: string }[] = [
  { icon: 'trophy', color: '#FF6B35', key: 'feature1' },
  { icon: 'star', color: '#FFD700', key: 'feature2' },
  { icon: 'flash', color: '#4ECDC4', key: 'feature3' },
];

export interface WelcomeScreenProps {
  isDark: boolean;
  onGetStarted: () => void;
  onSignIn: () => void;
  colors: {
    background: string;
    text: string;
    textSecondary: string;
    primary: string;
    primaryText: string;
    border: string;
    surface: string;
  };
}

export const WelcomeScreen = ({ isDark, onGetStarted, onSignIn, colors }: WelcomeScreenProps) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const styles = createStyles(colors);

  return (
    <View style={[styles.container, { paddingTop: insets.top, paddingBottom: Math.max(insets.bottom, 16) }]}>
      <View style={styles.inner}>
        {/* ── Header ── */}
        <View style={styles.header}>
          <View style={styles.headerLogo}>
            <Image
              source={require('@/assets/images/android-icon-foreground.png')}
              style={styles.logoIcon}
            />
            <Text style={styles.logoText}>Tournaly</Text>
          </View>
          <TouchableOpacity onPress={onSignIn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Text style={styles.signInLink}>{t('welcome.signIn')}</Text>
          </TouchableOpacity>
        </View>

        {/* ── Hero image ── */}
        <View style={styles.heroContainer}>
          <View style={styles.imageFrame}>
            <Image
              source={
                isDark
                  ? require('@/assets/images/screenshots/dark_bracket.png')
                  : require('@/assets/images/screenshots/light_bracket.png')
              }
              style={styles.heroImage}
              resizeMode="cover"
            />
          </View>
        </View>

        {/* ── Content ── */}
        <View style={styles.content}>
          <Text style={styles.headline}>{t('welcome.headline')}</Text>

          <View style={styles.features}>
            {FEATURES.map((f) => (
              <View key={f.key} style={styles.featureRow}>
                <View style={[styles.featureIconWrap, { backgroundColor: f.color + '22' }]}>
                  <Ionicons name={f.icon} size={15} color={f.color} />
                </View>
                <Text style={styles.featureText}>{t(`welcome.${f.key}`)}</Text>
              </View>
            ))}
          </View>

          <TouchableOpacity style={styles.primaryBtn} onPress={onGetStarted} activeOpacity={0.85}>
            <Text style={styles.primaryBtnText}>{t('welcome.getStarted')}</Text>
          </TouchableOpacity>

          <View style={styles.secondaryRow}>
            <Text style={styles.secondaryText}>{t('welcome.alreadyHaveAccount')}</Text>
            <TouchableOpacity onPress={onSignIn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Text style={styles.signInBtn}>{t('welcome.signIn')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};
